<template>
  <div class="card border mb-3">
    <div
      class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2"
    >
      <h5 class="mb-0">{{ $t("forwards.deals.metadata.title") }}</h5>
      <button
        v-if="deal.capabilities.can_manage"
        type="button"
        class="btn btn-sm btn-ghost"
        @click="emit('edit-metadata')"
      >
        {{ $t("forwards.deals.metadata.edit") }}
      </button>
    </div>

    <div class="card-body">
      <dl class="row mb-0">
        <dt class="col-sm-4">{{ $t("forwards.deals.metadata.buyer") }}</dt>
        <dd class="col-sm-8">
          {{ deal.counterparty_name ?? $t("forwards.deals.metadata.not_set") }}
          <span
            v-if="deal.counterparty_eecs_account_number"
            class="text-body-tertiary fs-10"
          >
            · {{ deal.counterparty_eecs_account_number }}</span
          >
          <span class="text-body-tertiary fs-10">
            · {{ deal.counterparty_registration_authority }}</span
          >
        </dd>

        <dt class="col-sm-4">{{ $t("forwards.deals.metadata.emails") }}</dt>
        <dd class="col-sm-8">
          {{
            deal.counterparty_emails ?? $t("forwards.deals.metadata.not_set")
          }}
        </dd>

        <dt class="col-sm-4">
          {{ $t("forwards.deals.metadata.deal_volume") }}
        </dt>
        <dd class="col-sm-8">
          <span v-if="deal.volume_mwh !== null">{{
            $t("forwards.common.mwh_value", {
              value: formatNumberWithSpaces(deal.volume_mwh),
            })
          }}</span>
          <span v-else>{{ $t("forwards.deals.metadata.not_set") }}</span>
        </dd>

        <dt class="col-sm-4">{{ $t("forwards.deals.metadata.signed_at") }}</dt>
        <dd class="col-sm-8">
          {{
            deal.signed_at
              ? formatDate(deal.signed_at)
              : $t("forwards.deals.metadata.not_set")
          }}
        </dd>

        <dt class="col-sm-4">
          {{ $t("forwards.deals.metadata.created_at") }}
        </dt>
        <dd class="col-sm-8" data-testid="deal-created-at">
          {{ formatDateTimeIntoDate(deal.created_at) }}
        </dd>

        <template v-if="deal.confirmed_at">
          <dt class="col-sm-4">
            {{ $t("forwards.deals.metadata.confirmed_at") }}
          </dt>
          <dd class="col-sm-8" data-testid="deal-confirmed-at">
            {{ formatDateTimeIntoDate(deal.confirmed_at) }}
          </dd>
        </template>

        <dt class="col-sm-4">
          {{ $t("forwards.deals.metadata.description") }}
        </dt>
        <dd class="col-sm-8">
          {{ deal.description ?? $t("forwards.deals.metadata.not_set") }}
          <div class="form-text mt-0">
            {{ $t("forwards.common.deal_text.description_help") }}
          </div>
        </dd>

        <dt class="col-sm-4">
          {{ $t("forwards.common.deal_text.internal_notes") }}
        </dt>
        <dd class="col-sm-8" data-testid="deal-internal-notes">
          <span v-if="deal.internal_notes" class="notes text-break"
            ><template
              v-for="(segment, index) in textWithLinks(deal.internal_notes)"
              :key="index"
              ><a
                v-if="segment.href"
                :href="segment.href"
                target="_blank"
                rel="noopener"
                >{{ segment.text }}</a
              ><template v-else>{{ segment.text }}</template></template
            ></span
          >
          <span v-else>{{ $t("forwards.deals.metadata.not_set") }}</span>
          <div class="form-text mt-0">
            {{ $t("forwards.common.deal_text.internal_notes_help") }}
          </div>
        </dd>

        <dt class="col-sm-4">{{ $t("forwards.deals.metadata.links") }}</dt>
        <dd class="col-sm-8">
          <div v-if="deal.contract_links" class="text-break">
            {{ $t("forwards.deals.metadata.contract_links") }}:
            {{ deal.contract_links }}
          </div>
          <div v-if="deal.transaction_link" class="text-break">
            {{ $t("forwards.deals.metadata.transaction_link") }}:
            <a
              v-if="isSafeUrl(deal.transaction_link)"
              :href="deal.transaction_link"
              target="_blank"
              rel="noopener"
              >{{ deal.transaction_link }}</a
            >
            <template v-else>{{ deal.transaction_link }}</template>
          </div>
          <span v-if="!deal.contract_links && !deal.transaction_link">{{
            $t("forwards.deals.metadata.not_set")
          }}</span>
        </dd>
      </dl>

      <hr />

      <div
        class="d-flex justify-content-between align-items-center flex-wrap gap-2"
      >
        <span class="eyebrow-label">{{
          $t("forwards.deals.criteria.title")
        }}</span>
        <button
          v-if="deal.capabilities.can_edit_criteria && !editingCriteria"
          type="button"
          class="btn btn-sm btn-ghost"
          :disabled="busyAction !== null"
          @click="startEditingCriteria"
        >
          {{ $t("forwards.deals.criteria.edit") }}
        </button>
        <span v-else-if="!deal.capabilities.can_edit_criteria" class="fs-10">
          <span
            class="badge rounded-pill bg-secondary-subtle text-secondary-emphasis"
            >{{ $t("forwards.deals.criteria.locked") }}</span
          >
        </span>
      </div>

      <dl v-if="!editingCriteria" class="row mb-0 mt-2">
        <dt class="col-sm-4">{{ $t("forwards.deals.criteria.countries") }}</dt>
        <dd class="col-sm-8">{{ criteriaText(deal.criteria.countries) }}</dd>
        <dt class="col-sm-4">
          {{ $t("forwards.deals.criteria.technologies") }}
        </dt>
        <dd class="col-sm-8">{{ technologiesText }}</dd>
        <dt class="col-sm-4">{{ $t("forwards.deals.criteria.cod") }}</dt>
        <dd class="col-sm-8">{{ codText }}</dd>
        <dt class="col-sm-4">{{ $t("forwards.deals.criteria.support") }}</dt>
        <dd class="col-sm-8">{{ supportText }}</dd>
        <dt class="col-sm-4">{{ $t("forwards.deals.criteria.ecolabels") }}</dt>
        <dd class="col-sm-8">{{ criteriaText(deal.criteria.ecolabels) }}</dd>
      </dl>

      <div v-else class="row g-3 mt-0">
        <div class="col-md-6">
          <label class="form-label" :for="countriesId">{{
            $t("forwards.deals.criteria.countries")
          }}</label>
          <MultiSelectComponent
            v-model="draft.countries"
            :buttonId="countriesId"
            :options="countryOptions"
            size="sm"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label" :for="technologiesId">{{
            $t("forwards.deals.criteria.technologies")
          }}</label>
          <MultiSelectComponent
            v-model="draft.technologies"
            :buttonId="technologiesId"
            :options="technologyOptions"
            size="sm"
          />
        </div>
        <div class="col-md-3">
          <label class="form-label" :for="codFromId">{{
            $t("forwards.deals.criteria.cod_from")
          }}</label>
          <input
            :id="codFromId"
            v-model="codFrom"
            type="date"
            class="form-control form-control-sm"
          />
        </div>
        <div class="col-md-3">
          <label class="form-label" :for="codUntilId">{{
            $t("forwards.deals.criteria.cod_until")
          }}</label>
          <input
            :id="codUntilId"
            v-model="codUntil"
            type="date"
            class="form-control form-control-sm"
          />
        </div>
        <div class="col-md-3">
          <label class="form-label" :for="supportId">{{
            $t("forwards.deals.criteria.support")
          }}</label>
          <select
            :id="supportId"
            v-model="supportChoice"
            class="form-select form-select-sm"
          >
            <option value="any">
              {{ $t("forwards.deals.criteria.support_any") }}
            </option>
            <option value="unsupported">
              {{ $t("forwards.deals.criteria.support_unsupported_only") }}
            </option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label" :for="ecolabelsId">{{
            $t("forwards.deals.criteria.ecolabels")
          }}</label>
          <MultiSelectComponent
            v-model="draft.ecolabels"
            :buttonId="ecolabelsId"
            :options="ecolabelOptions"
            size="sm"
          />
        </div>
        <div
          v-if="currentPreview && currentPreview.blocking.length > 0"
          class="col-12"
          data-testid="criteria-blocking"
        >
          <div class="alert alert-subtle-danger mb-0 py-2 fs-9">
            <p class="mb-1 fw-semibold">
              {{ $t("forwards.deals.criteria.conflicts_blocking") }}
            </p>
            <ul class="mb-0 ps-3">
              <li
                v-for="(conflict, index) in currentPreview.blocking"
                :key="index"
              >
                {{ conflict.message }}
              </li>
            </ul>
          </div>
        </div>
        <div
          v-if="currentPreview && currentPreview.warnings.length > 0"
          class="col-12"
          data-testid="criteria-warnings"
        >
          <div class="alert alert-subtle-warning mb-0 py-2 fs-9">
            <p class="mb-1 fw-semibold">
              {{ $t("forwards.deals.criteria.conflicts_warning") }}
            </p>
            <ul class="mb-0 ps-3">
              <li
                v-for="(conflict, index) in currentPreview.warnings"
                :key="index"
              >
                {{ conflict.message }}
              </li>
            </ul>
          </div>
        </div>
        <div class="col-12 d-flex justify-content-end gap-2">
          <button
            type="button"
            class="btn btn-sm btn-link text-secondary"
            @click="editingCriteria = false"
          >
            {{ $t("forwards.deals.common.cancel") }}
          </button>
          <ActionButtonWithSpinner
            :busy="busyAction === 'criteria'"
            :disabled="busyAction !== null"
            variant="btn-primary"
            @click="saveCriteria"
          >
            {{
              acknowledgesWarnings
                ? $t("forwards.deals.criteria.save_anyway")
                : $t("forwards.deals.criteria.save")
            }}
          </ActionButtonWithSpinner>
        </div>
        <p class="col-12 form-text mb-0">
          {{ $t("forwards.deals.criteria.conflicts_help") }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import {
  formatDate,
  formatDateTimeIntoDate,
  formatNumberWithSpaces,
} from "@/composables/formatUtils";
import type { DealAction } from "@/forwards2/deals/useDealStore";
import {
  ECOLABEL_KEYS,
  type CriteriaPreview,
  type DealResponse,
  type EligibilityCriteriaRequest,
} from "@/forwards2/forwardsTypes";
import { TECHNOLOGIES, technologyNameForCode } from "@/types/technology";
import { ALL_COUNTRIES } from "@/utils/countries";
import { isSafeUrl, textWithLinks } from "@/utils/urlUtils";
import { isEqual } from "lodash-es";
import { computed, ref, useId } from "vue";
import { useI18n } from "vue-i18n";

/**
 * What was sold, to whom, and under which product definition.
 *
 * The criteria are the deal's product: a draft's owner may change them, a confirmed deal's only an
 * admin, and never in a way that conflicts with backing or escrow the deal still delivers. Saving
 * first shows the parent's dry run of the entered criteria: blocking conflicts to clear elsewhere
 * on the page, or warnings the next press saves over ("Save anyway"). A preview of other criteria
 * than the ones entered is stale and not shown.
 */
const props = withDefaults(
  defineProps<{
    deal: DealResponse;
    busyAction?: DealAction | null;
    criteriaPreview?: CriteriaPreview | null;
  }>(),
  { busyAction: null, criteriaPreview: null }
);

const emit = defineEmits<{
  "edit-metadata": [];
  /** `acknowledged`: the entered criteria were previewed with warnings only, so this press saves over them. */
  "save-criteria": [body: EligibilityCriteriaRequest, acknowledged: boolean];
}>();

/**
 * Owned by the parent, which closes the editor only once the save has landed: a criteria change the
 * backend refuses (a deal confirmed since the page loaded) must keep the entered values on screen,
 * and the busy spinner must have a form to sit in (RV2-plan-editing-05).
 */
const editingCriteria = defineModel<boolean>("editingCriteria", {
  required: true,
});

const { t } = useI18n();

const countriesId = useId();
const technologiesId = useId();
const codFromId = useId();
const codUntilId = useId();
const supportId = useId();
const ecolabelsId = useId();

const draft = ref<{
  countries: string[];
  technologies: string[];
  ecolabels: string[];
}>({ countries: [], technologies: [], ecolabels: [] });
const codFrom = ref("");
const codUntil = ref("");
const supportChoice = ref<"any" | "unsupported">("any");

const criteriaBody = computed<EligibilityCriteriaRequest>(() => ({
  countries: draft.value.countries,
  technologies: draft.value.technologies,
  cod_from: codFrom.value === "" ? null : codFrom.value,
  cod_until: codUntil.value === "" ? null : codUntil.value,
  support: supportChoice.value === "unsupported" ? false : null,
  ecolabels: draft.value.ecolabels,
}));

const currentPreview = computed(() =>
  props.criteriaPreview !== null &&
  isEqual(props.criteriaPreview.body, criteriaBody.value)
    ? props.criteriaPreview
    : null
);

const acknowledgesWarnings = computed(
  () =>
    currentPreview.value !== null &&
    currentPreview.value.blocking.length === 0 &&
    currentPreview.value.warnings.length > 0
);

const countryOptions = computed(() => ALL_COUNTRIES);
// The criteria are stored and matched as AIB code prefixes, so the option's value is the prefix and
// only its label is the technology's name (RV-picker-01).
const technologyOptions = computed(() =>
  Object.values(TECHNOLOGIES).map((technology) => ({
    value: technology.prefix,
    label: technology.name,
  }))
);
const ecolabelOptions = computed(() =>
  ECOLABEL_KEYS.map((key) => ({
    value: key as string,
    label: t(`forwards.deals.criteria.ecolabel.${key}`),
  }))
);

// The criteria travel and are stored as AIB code prefixes, so the read view has to turn them back
// into the technology's name — "T02" means nothing to a trader (RV-picker-01).
const technologiesText = computed(() =>
  criteriaText(
    props.deal.criteria.technologies?.map(technologyNameForCode) ?? null
  )
);

const criteriaText = (values: string[] | null) =>
  values === null || values.length === 0
    ? t("forwards.deals.criteria.unrestricted")
    : values.join(", ");

const codText = computed(() => {
  const { cod_from: from, cod_until: until } = props.deal.criteria;
  if (from === null && until === null)
    return t("forwards.deals.criteria.unrestricted");
  return t("forwards.deals.criteria.cod_range", {
    from: from ? formatDate(from) : t("forwards.deals.criteria.any"),
    until: until ? formatDate(until) : t("forwards.deals.criteria.any"),
  });
});

const supportText = computed(() =>
  props.deal.criteria.support === false
    ? t("forwards.deals.criteria.support_unsupported_only")
    : t("forwards.deals.criteria.unrestricted")
);

const startEditingCriteria = () => {
  const criteria = props.deal.criteria;
  draft.value = {
    countries: [...(criteria.countries ?? [])],
    technologies: [...(criteria.technologies ?? [])],
    ecolabels: [...(criteria.ecolabels ?? [])],
  };
  codFrom.value = criteria.cod_from ?? "";
  codUntil.value = criteria.cod_until ?? "";
  supportChoice.value = criteria.support === false ? "unsupported" : "any";
  editingCriteria.value = true;
};

const saveCriteria = () => {
  emit("save-criteria", criteriaBody.value, acknowledgesWarnings.value);
};
</script>

<style lang="scss" scoped>
// v1 comments migrated into the notes keep their line breaks.
.notes {
  white-space: pre-wrap;
}
</style>
